import {getLocale} from 'epic-common/src/locale';

export enum TimeInSeconds {
    SECOND = 1,
    MINUTE = 60,
    HOUR = MINUTE * 60,
    DAY = HOUR * 24,
    MONTH = DAY * 30,
    YEAR = DAY * 365
}

export type DateType = Date | string | number;

export const inferDate = (value: DateType): Date =>
    value instanceof Date ? value : new Date(value);

export const getUTCTime = (value: Date): number => {
    return Date.UTC(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        value.getHours(),
        value.getMinutes(),
        value.getSeconds(),
        value.getMilliseconds()
    );
};

export const dateFormatUTC = (timestamp: DateType, locale?: string): string => {
    if (!timestamp) return undefined;
    const fallbackLocale = 'en-US';
    const _locale = locale || getLocale() || fallbackLocale;
    try {
        return Intl.DateTimeFormat([_locale, fallbackLocale], {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: 'UTC'
        }).format(inferDate(timestamp));
    } catch (err) {
        console.error(`Invalid Date Format: ${timestamp}`);
        return undefined;
    }
};

export const compareDate = (dateA: DateType, dateB: DateType) => {
    return inferDate(dateA).valueOf() - inferDate(dateB).valueOf();
};

export const getDateDiff = (
    dateA: DateType,
    dateB: DateType,
    timeInSeconds: TimeInSeconds
): number => {
    try {
        const utc1 = getUTCTime(inferDate(dateA));
        const utc2 = getUTCTime(inferDate(dateB));
        return Math.floor((utc2 - utc1) / (timeInSeconds * 1000));
    } catch (err) {
        console.error(`Invalid dates given: ${err.message}`);
        return undefined;
    }
};

/**
 * @param timestamp - date
 * @param increaseYear - start from prev year
 * @returns YYYY-MM-DD
 */
export const formatDate = (timestamp: DateType, increaseYear = false) => {
    let date = timestamp;

    if (!(date instanceof Date)) {
        date = new Date(timestamp);
    }

    if (increaseYear) {
        date.setFullYear(date.getFullYear() - 1);
    }

    let day: string | number = date.getDate();
    let month: string | number = date.getMonth() + 1;
    let year: string | number = date.getFullYear();

    if (day < 10) {
        day = `0${day}`;
    }

    if (month < 10) {
        month = `0${month}`;
    }

    if (year < 10) {
        year = `0${year}`;
    }

    return `${year}-${month}-${day}`;
};
